import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { doc, setDoc, collection, getDoc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import '../styles/Signup.css'; // Reuse signup styles

function CompanySetup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    industry: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const industries = [
    'Construction',
    'Manufacturing',
    'Transportation & Logistics',
    'Healthcare',
    'Mining',
    'Energy & Utilities',
    'Agriculture',
    'Hospitality',
    'Retail',
    'Warehousing',
    'Emergency Services',
    'Aviation',
    'Maritime',
    'Other'
  ];

  const generateCompanyCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const generateUniqueCompanyCode = async () => {
    const maxAttempts = 10;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const code = generateCompanyCode();
      const codeRef = doc(db, 'companyCodes', code);
      const codeDoc = await getDoc(codeRef);
      
      if (!codeDoc.exists()) {
        return code;
      }
    }
    throw new Error('Unable to generate unique company code. Please try again.');
  };

  const sanitizeCompanyName = (name) => {
    return name.trim().slice(0, 100).replace(/[<>]/g, '');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const sanitizedCompanyName = sanitizeCompanyName(formData.companyName);
    if (!sanitizedCompanyName || sanitizedCompanyName.length < 2) {
      setError('Company name must be at least 2 characters');
      return;
    }

    if (!formData.industry) {
      setError('Please select an industry');
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Generate unique company code
      const companyCode = await generateUniqueCompanyCode();

      // Create company document
      const companyRef = doc(collection(db, 'companies'));
      await setDoc(companyRef, {
        name: sanitizedCompanyName,
        industry: formData.industry,
        companyCode: companyCode,
        adminId: user.uid,
        createdAt: new Date().toISOString()
      });

      // Update admin document (replace "Null" values)
      await updateDoc(doc(db, 'admins', user.uid), {
        companyId: companyRef.id,
        companyCode: companyCode,
        createdAt: new Date().toISOString()
      });

      // Update users document (add companyId)
      await updateDoc(doc(db, 'users', user.uid), {
        companyId: companyRef.id
      });

      // Create company code lookup document
      await setDoc(doc(db, 'companyCodes', companyCode), {
        companyId: companyRef.id,
        companyName: sanitizedCompanyName,
        industry: formData.industry,
        createdAt: new Date().toISOString()
      });

      alert(
        `Company created successfully!\n\n` +
        `Company Code: ${companyCode}\n\n` +
        `Share this code with employees so they can join your company!`
      );

      // Navigate to dashboard
      navigate('/dashboard');

    } catch (err) {
      console.error('Company setup error:', err);
      
      if (err.message?.includes('unique company code')) {
        setError('Unable to generate company code. Please try again.');
      } else if (err.code === 'permission-denied') {
        setError('Permission denied. Please contact support.');
      } else {
        setError('Failed to create company. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-header">
          <h1>Zella</h1>
          <h2>Create Your Company</h2>
          <p>Set up your company dashboard</p>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="info-box" style={{
          background: '#e3f2fd',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px',
          border: '1px solid #2196f3'
        }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#1565c0' }}>
            ℹ️ You signed up via the mobile app. Complete this setup to access the admin dashboard.
          </p>
        </div>
        
        <form className="signup-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="companyName">Company Name</label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Acme Corporation"
              maxLength="100"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="industry">Industry</label>
            <select
              id="industry"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              required
            >
              <option value="">Select your industry</option>
              {industries.map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
          </div>
          
          <button type="submit" className="btn-signup-submit" disabled={loading}>
            {loading ? 'Creating Company...' : 'Create Company'}
          </button>
        </form>
        
        <div className="back-to-home">
          <button 
            onClick={() => {
              auth.signOut();
              navigate('/');
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            ← Log out
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompanySetup;